// SendDialog component with WYSIWYG editor - All German
import { useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Send, Mail, Image, Loader2, Info } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { AttachmentPicker } from './AttachmentPicker';

export function SendDialog({ open, onClose, form, setForm, images, fileRef, onUpload, progress, sending, onTest, onSend, count, apiUrl, attachments, setAttachments }) {
  const quillRef = useRef(null);
  
  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list',
    'align',
    'link', 'image'
  ];

  // Insert variable into content
  const insertVariable = (variable) => {
    setForm({ ...form, html_content: form.html_content + variable });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>E-Mail-Kampagne</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Absender E-Mail</Label>
              <Input 
                type="email" 
                value={form.sender_email} 
                onChange={e => setForm({ ...form, sender_email: e.target.value })} 
                placeholder="noreply@vitalis-sports.ch"
              />
            </div>
            <div>
              <Label>Absender Name</Label>
              <Input 
                value={form.sender_name} 
                onChange={e => setForm({ ...form, sender_name: e.target.value })} 
                placeholder="Vitalis Sports"
              />
            </div>
          </div>
          <div>
            <Label>Betreff</Label>
            <Input 
              value={form.subject} 
              onChange={e => setForm({ ...form, subject: e.target.value })} 
              placeholder="Betreff Ihrer E-Mail"
            />
          </div>
          
          {/* Personalization variables section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-blue-600" />
              <Label className="text-blue-800 font-medium">Personalisierungsvariablen</Label>
            </div>
            <p className="text-sm text-blue-700 mb-2">Klicken Sie auf eine Variable, um sie in Ihren Inhalt einzufügen:</p>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => insertVariable('{{anrede_nachname}}')}
                className="bg-white hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                {'{{anrede_nachname}}'} 
                <span className="ml-1 text-xs text-muted-foreground">(z.B. Herr Müller)</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => insertVariable('{{vorname}}')}
                className="bg-white hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                {'{{vorname}}'}
                <span className="ml-1 text-xs text-muted-foreground">(z.B. Hans)</span>
              </Button>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Inhalt</Label>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Image className="h-4 w-4 mr-1" />
                Bild hinzufügen
              </Button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
            </div>
            <div className="border rounded-md">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={form.html_content}
                onChange={(content) => setForm({ ...form, html_content: content })}
                modules={modules}
                formats={formats}
                placeholder="Schreiben Sie Ihre Nachricht hier..."
                style={{ minHeight: '200px' }}
              />
            </div>
          </div>
          
          {/* Image gallery */}
          {images && images.length > 0 && (
            <div>
              <Label className="text-sm text-muted-foreground">Hochgeladene Bilder (klicken zum Einfügen)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {images.map(img => (
                  <div 
                    key={img.id} 
                    className="w-16 h-16 border rounded cursor-pointer hover:border-primary overflow-hidden"
                    onClick={() => {
                      const imgTag = `<img src="${apiUrl}${img.url}" style="max-width:100%;" />`;
                      setForm({ ...form, html_content: form.html_content + imgTag });
                    }}
                  >
                    <img 
                      src={`${apiUrl}${img.url}`} 
                      alt={img.filename} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* File attachments library */}
          <div className="border-t pt-4">
            <AttachmentPicker value={attachments} onChange={setAttachments} />
          </div>
          
          {/* Progress bar */}
          {progress && (
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span>Wird gesendet...</span>
                <span className="font-medium">{progress.sent}/{progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-primary h-2.5 rounded-full transition-all" 
                  style={{ width: `${(progress.sent/progress.total)*100}%` }} 
                />
              </div>
              {progress.failed > 0 && (
                <p className="text-red-500 text-sm mt-2">{progress.failed} Fehler</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onTest} disabled={sending}>
            <Mail className="h-4 w-4 mr-1" />
            Testen
          </Button>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Schliessen
          </Button>
          <Button onClick={onSend} disabled={sending || !form.subject.trim()}>
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Send className="h-4 w-4 mr-1" />
            )}
            An {count} Kontakt(e) senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
